import { Storage } from 'aws-amplify';
import imageCompression from 'browser-image-compression';
import localForage from 'localforage';
import path from 'path';
import { v4 as uuidv4 } from 'uuid';

import { useMst } from '../mobx-models/Root';
import {
  AimsFile,
  DeficiencyFileEdited,
  FileOperation,
  InspectionFilesEdited,
  ISignAndStampData,
} from '../models/models';
import { DeficiencyFiles } from '../rxdb/deficiencyFiles/queryBuilder';
import { useDeficiencyFiles } from '../rxdb/deficiencyFiles/useDeficiencyFiles';
import { useDeficiencyVersionFiles } from '../rxdb/deficiencyVersionFiles/useDeficiencyVersionFiles';
import { InspectionFiles } from '../rxdb/inspectionFiles/queryBuilder';
import { useInspectionFiles } from '../rxdb/inspectionFiles/useInspectionFiles';
import { LiveInterval } from '../rxdb/LiveInterval';
import { SignAndStampFile } from '../rxdb/signAndStamp/queryBuilder';
import { useSignAndStampFile } from '../rxdb/signAndStamp/useSignAndStampFile';
import { FALSE } from './constants';
import { FileCategory } from './enums';
import { sentryCaptureException } from './index';
import { useOnlineStatus } from './useOnlineStatus';
import {
  getContentType,
  getLocalStorageCurrentUserId,
  getLocalStorageSelectedDisciplineId,
  getLocalStorageSelectedSiteId,
  getValueFromLocalStorage,
  setDeltaFileData,
} from './utilityFunctions';

const store = localForage.createInstance({
  name: 'imageUploadQueue',
  storeName: 'imageUploadQueue',
});

const attachmentStore = localForage.createInstance({
  name: 'attachment-store',
});

const activeDeficiencyFileStore = localForage.createInstance({
  name: 'activeDeficiencyFileStore',
});

interface UploadItem {
  protectionLevel: string;
  uploadPath: string;
  fileObj: InspectionFiles;
  deficiencyFileObj: DeficiencyFiles;
  arrayBuffer: ArrayBuffer;
  type: string;
  fileUuid: string;
}

const defaultProtectionLevel = 'public';
const maxImageSizeMB = 2;

const arrayBufferToBlob = (buffer: ArrayBuffer, type: string) => {
  return new Blob([buffer], { type });
};

export const getImage = (fileKey: string, protectionLevel = defaultProtectionLevel) => {
  return Storage.get(fileKey, { level: protectionLevel });
};

const getImageType = (contentType: string) => {
  if (contentType === 'image/jpeg' || contentType === 'image/png' || contentType === 'image/jpg') {
    return true;
  } else {
    return false;
  }
};

export const useFileStorage = () => {
  const online = useOnlineStatus();
  const options = {
    maxSizeMB: maxImageSizeMB,
    useWebWorker: true,
  };

  const addFileToUploadCache = async (
    fileUuid: string,
    uploadPath: string,
    fileObj: object,
    blob: Blob,
    protectionLevel: string,
    type: string,
  ) => {
    await store.setItem(fileUuid, {
      protectionLevel,
      uploadPath,
      fileObj,
      type,
      fileUuid,
    });
    await attachmentStore.setItem(fileUuid, blob);
  };
  const { insertIntoInspectionFileRxdb, updateInspectionFile } = useInspectionFiles();
  const { insertIntoSignAndStampFileRxdb } = useSignAndStampFile();
  const { insertFileIntoDeficiencyFile } = useDeficiencyFiles();
  const { insertFileIntoActiveDeficiencyFile } = useDeficiencyVersionFiles();
  const { UploadFileProgress, HandleGesture, CurrentUserSelected, SiteDiscipline } = useMst();

  const setUpLoadProgress = async (fileUUId: string, value: number) => {
    UploadFileProgress.setUploadValue(fileUUId, value);
  };

  const removeItemFromUploadCache = (key: string) => store.removeItem(key);

  const deleteExpiredItems = async () => {
    const keys = await activeDeficiencyFileStore.keys();
    const now = Date.now();
    const sevenDays = LiveInterval.DAY_1 * 7;

    for (const key of keys) {
      const item = await activeDeficiencyFileStore.getItem(key);
      if (item && item.timestamp && now - item.timestamp > sevenDays) {
        attachmentStore.removeItem(key).then(() => activeDeficiencyFileStore.removeItem(key));
      }
    }
  };

  setInterval(deleteExpiredItems, LiveInterval.DAY_1);

  const uploadCachedImage = async (
    uploadPath: string,
    blob: Blob,
    fileObj: InspectionFiles,
    deficiencyFileObj: DeficiencyFiles,
    protectionLevel: string,
    type: string,
    key: string,
  ) => {
    try {
      if (fileObj?.fileCategory === FileCategory.INSPECTION_FILE) {
        await Storage.put(uploadPath, blob, {
          contentType: type,
          level: protectionLevel,
          progressCallback(progress: { loaded: number; total: number }) {
            if (!fileObj?.extension?.startsWith('image')) {
              setUpLoadProgress(
                fileObj?.fileUUID,
                Math.floor((progress.loaded / progress.total) * 100),
              );
            }
          },
        });
      } else {
        await Storage.put(uploadPath, blob, {
          contentType: type,
          level: protectionLevel,
        });
      }
      removeItemFromUploadCache(key);
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useFileStorage.tsx ~ line 134 ~ uploadCachedImage- ~',
        'uploadCachedImage',
        JSON.stringify({
          blob:
            blob !== null && blob !== undefined
              ? JSON.stringify(blob)
              : 'Blob is null or undefined',
          error:
            error !== null && error !== undefined
              ? JSON.stringify(error)
              : 'Error is null or undefined',
        }),
      );
    }
  };

  const getAttachmentFromIndexDB = (attachmentId: string) => attachmentStore.getItem(attachmentId);

  const uploadOneFileFromCache = async (key: string) => {
    const value: UploadItem | null = await store.getItem(key);
    if (value === null) return;
    const { protectionLevel, uploadPath, fileObj, deficiencyFileObj, type, fileUuid } = value;
    const blob = (await getAttachmentFromIndexDB(fileUuid)) as Blob;

    try {
      await uploadCachedImage(
        uploadPath,
        blob,
        fileObj,
        deficiencyFileObj,
        protectionLevel,
        type,
        key,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useFileStorage.tsx ~ line 196 ~ uploadOneFileFromCache ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useFileStorage.tsx ~ line 196 ~ uploadCachedImage- ~',
        'uploadCachedImage',
        JSON.stringify({
          blob:
            blob !== null && blob !== undefined
              ? JSON.stringify(blob)
              : 'Blob is null or undefined',
          error:
            error !== null && error !== undefined
              ? JSON.stringify(error)
              : 'Error is null or undefined',
        }),
      );
    }
  };

  const uploadFilesInCache = async () => {
    const uploadKeys = await store.keys();

    await Promise.all(uploadKeys.map(key => uploadOneFileFromCache(key)));
  };

  const getImageInUploadQueue = async (fileKey: string): Promise<Blob | undefined> => {
    const value: UploadItem | null = await store.getItem(fileKey);
    if (value === null) return;

    const { arrayBuffer } = value;
    const blob = arrayBufferToBlob(arrayBuffer, 'image/jpeg');

    return blob;
  };

  const clearAttachmentStore = () => {
    attachmentStore.clear();
  };

  const uploadDeficiencyFile = async (
    file: AimsFile,
    deficiencyId: string,
    deficiencyUuid: string,
    isReInspectedFile: boolean,
    componentVersionInspectionId: number | null,
    componentVersionId: number | null,
    isAdhoc: boolean,
    deficiencyfileData: DeficiencyFileEdited | undefined = undefined,
    protectionLevel = defaultProtectionLevel,
  ) => {
    const fileData = deficiencyfileData ? deficiencyfileData : file;
    const localStorageSelectedSiteId = getLocalStorageSelectedSiteId();
    const localStorageSelectedDisciplineId = getLocalStorageSelectedDisciplineId();
    let { ext } = path.parse(
      deficiencyfileData
        ? (fileData as DeficiencyFileEdited).fileName
        : (fileData as AimsFile).updatedName,
    );
    ext = ext?.toLowerCase();
    const contentType = getContentType(ext);

    let compressedFile = file;
    if (contentType === 'image/jpeg' || contentType === 'image/png') {
      compressedFile = (await imageCompression(file, options)) as AimsFile;
    }
    const blob = new Blob([compressedFile], { type: contentType });
    let uploadPath = '';
    let deficiencyFilePayload: DeficiencyFileEdited = {} as DeficiencyFileEdited;

    if (deficiencyfileData) {
      if (deficiencyfileData.operation === FileOperation.fileCopy) {
        uploadPath = `deficiency/${deficiencyfileData.deficiencyUUID}/${
          deficiencyfileData.fileName
        }-${Date.now()}${ext}`;
        deficiencyFilePayload = {
          fileId: `0`,
          fileName: `${deficiencyfileData.fileName}`,
          fileAimsUUID: isReInspectedFile ? deficiencyfileData?.fileAimsUUID : uuidv4(),
          deficiencyUUID: `${deficiencyfileData.deficiencyUUID}`,
          originalFileName: `${deficiencyfileData.originalFileName}`,
          path: `${uploadPath}`,
          legacyId: 0,
          isImageFile: getImageType(contentType),
          inspectionId: isAdhoc ? null : `${CurrentUserSelected.inspectionId}`,
          assetId: CurrentUserSelected.assetId,
          deficiencyId: `${deficiencyfileData.deficiencyId}`,
          showOnReport: fileData.showOnReport,
          fileDescription: fileData.fileDescription || '',
          drawingType: 11,
          fileCategory: 9,
          pcsId: isAdhoc ? null : `${CurrentUserSelected.inspectionId}`,
          fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
          sizeMb: blob?.size,
          allowSpaccess: true,
          siteId: Number(localStorageSelectedSiteId),
          disciplineId: Number(localStorageSelectedDisciplineId),
          userCreated: '',
          userModified: '',
          isReInspectedFile: isReInspectedFile || false,
          extension: blob?.type,
          isDelete: deficiencyfileData.isDelete,
          fileSortOrder: fileData.fileSortOrder,
          assetInspectionTask: isAdhoc ? null : CurrentUserSelected.taskId,
          fkInspectorField: !isNaN(Number(getValueFromLocalStorage('taskFieldInspectorId')))
            ? Number(getValueFromLocalStorage('taskFieldInspectorId'))
            : 0,
          fkInspectorLead: Number(getValueFromLocalStorage('taskLeadInspectorId')),
          componentVersionInspectionId,
          componentVersionId,
          fileComponentVersionUUId: uuidv4(),
        };
      } else if (deficiencyfileData.operation === FileOperation.fileBlobEdited) {
        uploadPath = deficiencyfileData.path;
        deficiencyFilePayload = deficiencyfileData;
      }
      delete deficiencyFilePayload.operation;
    } else {
      uploadPath = `deficiency/${deficiencyUuid}/${
        (fileData as AimsFile).updatedName
      }-${Date.now()}${ext}`;
      deficiencyFilePayload = {
        fileId: `0`,
        fileName: `${(fileData as AimsFile).updatedName}`,
        fileAimsUUID: uuidv4(),
        deficiencyUUID: `${deficiencyUuid}`,
        originalFileName: `${(fileData as AimsFile).name}`,
        path: `${uploadPath}`,
        legacyId: 0,
        isImageFile: getImageType(contentType),
        inspectionId: isAdhoc ? null : `${CurrentUserSelected.inspectionId}`,
        assetId: CurrentUserSelected.assetId,
        deficiencyId: `${deficiencyId !== undefined ? deficiencyId : '0'}`,
        showOnReport: fileData?.showOnReport ? fileData?.showOnReport : false,
        fileDescription: `${fileData?.fileDescription ? fileData?.fileDescription : ''}`,
        drawingType: 11,
        fileCategory: 9,
        pcsId: isAdhoc ? null : `${CurrentUserSelected.inspectionId}`,
        fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        sizeMb: blob?.size,
        allowSpaccess: true,
        isReInspectedFile: isReInspectedFile || false,
        siteId: Number(localStorageSelectedSiteId),
        disciplineId: Number(localStorageSelectedDisciplineId),
        userCreated: '',
        userModified: '',
        extension: blob?.type,
        isDelete: FALSE,
        fileSortOrder: fileData.fileSortOrder,
        assetInspectionTask: isAdhoc ? null : CurrentUserSelected.taskId,
        fkInspectorField: !isNaN(Number(getValueFromLocalStorage('taskFieldInspectorId')))
          ? Number(getValueFromLocalStorage('taskFieldInspectorId'))
          : 0,
        fkInspectorLead: Number(getValueFromLocalStorage('taskLeadInspectorId')),
        componentVersionInspectionId,
        componentVersionId,
        fileComponentVersionUUId: uuidv4(),
      };
    }
    if (isReInspectedFile) {
      await insertFileIntoActiveDeficiencyFile(deficiencyFilePayload);
    } else {
      await insertFileIntoDeficiencyFile(deficiencyFilePayload);
    }
    await addFileToUploadCache(
      isReInspectedFile
        ? deficiencyFilePayload?.fileComponentVersionUUId
        : deficiencyFilePayload.fileAimsUUID,
      uploadPath,
      fileData,
      blob,
      protectionLevel,
      contentType,
    );
    if (!online) return { key: uploadPath };
    try {
      await Storage.put(uploadPath, blob, {
        contentType,
        level: protectionLevel,
      });
      await removeItemFromUploadCache(
        isReInspectedFile
          ? deficiencyFilePayload?.fileComponentVersionUUId
          : deficiencyFilePayload.fileAimsUUID,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('🚀 ~ file: useFileStorage.tsx ~ line 374 ~ useFileStorage ~ error', error);
      // console logs will be removed once sentry implementation is done

      sentryCaptureException(
        error,
        '~ Deficiency file: useFileStorage.tsx ~ line 374 ~ useFileStorage ~',
        'uploadDeficiencyFile',
        JSON.stringify({
          blob:
            blob !== null && blob !== undefined
              ? JSON.stringify(blob)
              : 'Blob is null or undefined',
          error:
            error !== null && error !== undefined
              ? JSON.stringify(error)
              : 'Error is null or undefined',
        }),
      );
      return { key: uploadPath };
    }
  };

  const uploadInspectionFile = async (
    file: AimsFile,
    inspectionFileData: InspectionFilesEdited | undefined = undefined,
    protectionLevel = defaultProtectionLevel,
  ) => {
    const fileData = inspectionFileData ? inspectionFileData : file;
    const localStorageSelectedSiteId = getLocalStorageSelectedSiteId();
    const localStorageSelectedDisciplineId = getLocalStorageSelectedDisciplineId();
    let { ext } = path.parse(file.name);
    ext = ext?.toLowerCase();
    const contentType = getContentType(ext);

    let compressedFile = file;
    if (contentType === 'image/jpeg' || contentType === 'image/png') {
      compressedFile = (await imageCompression(file, options)) as AimsFile;
    }
    const blob = new Blob([compressedFile], { type: contentType });
    let uploadPath = '';
    let inspectionFilePayload: InspectionFiles = {} as InspectionFiles;

    if (inspectionFileData) {
      uploadPath =
        inspectionFileData.operation === FileOperation.fileCopy
          ? `assetInspection/${CurrentUserSelected.inspectionId}/${file.name}-${Date.now()}${ext}`
          : inspectionFileData.originalPath;
      switch (inspectionFileData?.operation) {
        case FileOperation.fileCopy:
          delete (fileData as InspectionFilesEdited)?.operation;
          inspectionFilePayload = {
            fileId: `0`,
            name: `${file.name}`,
            fileUUID: uuidv4(),
            originalFileName: `${file.name}`,
            originalPath: `${uploadPath}`,
            legacyId: 0,
            isImageFile: getImageType(contentType),
            inspectionId: `${CurrentUserSelected.inspectionId}`,
            showOnReport: inspectionFileData.showOnReport,
            fileDescription: inspectionFileData.fileDescription,
            drawingType: 11,
            fileCategory: 8,
            pcsId: `${CurrentUserSelected.inspectionId}`,
            fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
            sizeMb: blob?.size,
            allowSpaccess: true,
            siteId: Number(localStorageSelectedSiteId),
            disciplineId: Number(localStorageSelectedDisciplineId),
            userCreated: '',
            userModified: '',
            extension: blob?.type,
            isDelete: FALSE,
            fkAssetInspectionTask: CurrentUserSelected.taskId,
            fkInspectorField: !isNaN(Number(getValueFromLocalStorage('taskFieldInspectorId')))
              ? Number(getValueFromLocalStorage('taskFieldInspectorId'))
              : 0,
            fkInspectorLead: Number(getValueFromLocalStorage('taskLeadInspectorId')),
            fileSortOrder: inspectionFileData.fileSortOrder,
          };
          break;
        case FileOperation.fileBlobEdited:
          inspectionFilePayload = JSON.parse(JSON.stringify(fileData));
          break;
      }
    } else {
      uploadPath = `assetInspection/${CurrentUserSelected.inspectionId}/${
        file.updatedName
      }-${Date.now()}${ext}`;
      inspectionFilePayload = {
        fileId: `0`,
        name: `${file.updatedName}`,
        fileUUID: uuidv4(),
        originalFileName: `${file.updatedName}`,
        originalPath: `${uploadPath}`,
        legacyId: 0,
        isImageFile: getImageType(contentType),
        inspectionId: `${CurrentUserSelected.inspectionId}`,
        showOnReport: file?.showOnReport ? file?.showOnReport : false,
        fileDescription: `${file?.fileDescription ? file?.fileDescription : ''}`,
        drawingType: 11,
        fileCategory: 8,
        pcsId: `${CurrentUserSelected.inspectionId}`,
        fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        sizeMb: blob?.size,
        allowSpaccess: true,
        siteId: Number(localStorageSelectedSiteId),
        disciplineId: Number(localStorageSelectedDisciplineId),
        userCreated: '',
        userModified: '',
        extension: blob?.type,
        isDelete: FALSE,
        fkAssetInspectionTask: CurrentUserSelected.taskId,
        fkInspectorField: !isNaN(Number(getValueFromLocalStorage('taskFieldInspectorId')))
          ? Number(getValueFromLocalStorage('taskFieldInspectorId'))
          : 0,
        fkInspectorLead: Number(getValueFromLocalStorage('taskLeadInspectorId')),
        fileSortOrder: file.fileSortOrder,
      };
    }

    await setDeltaFileData([
      { fileUUId: inspectionFilePayload.fileUUID, dateModified: '', isUploadedByUser: true },
    ]);
    await addFileToUploadCache(
      inspectionFilePayload.fileUUID,
      uploadPath,
      inspectionFilePayload,
      blob,
      protectionLevel,
      contentType,
    );
    if (inspectionFileData) {
      if (inspectionFileData?.operation === FileOperation?.fileBlobEdited) {
        updateInspectionFile(
          inspectionFilePayload.fileUUID,
          inspectionFilePayload.name,
          inspectionFilePayload.showOnReport,
          inspectionFilePayload.fileDescription,
          inspectionFilePayload.isDelete as string,
          inspectionFilePayload.fileSortOrder,
        );
        delete (fileData as InspectionFilesEdited)?.operation;
      } else {
        insertIntoInspectionFileRxdb([inspectionFilePayload]);
      }
    } else {
      return inspectionFilePayload;
    }

    if (!online) return { key: uploadPath };
    try {
      await Storage.put(uploadPath, blob, {
        contentType,
        level: protectionLevel,
        progressCallback(progress: { loaded: number; total: number }) {
          if (!inspectionFileData?.extension?.startsWith('image')) {
            setUpLoadProgress(
              inspectionFilePayload.fileUUID,
              Math.floor((progress.loaded / progress.total) * 100),
            );
          }
        },
      });
      removeItemFromUploadCache(inspectionFilePayload.fileUUID);
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useFileStorage.tsx ~ line 524 ~ useFileStorage ~',
        'uploadInspectionFile',
        JSON.stringify({
          blob:
            blob !== null && blob !== undefined
              ? JSON.stringify(blob)
              : 'Blob is null or undefined',
          error:
            error !== null && error !== undefined
              ? JSON.stringify(error)
              : 'Error is null or undefined',
        }),
      );
      return { key: uploadPath };
    }
  };

  const bulkUploadToS3 = async (
    uploadPath: string,
    file: File,
    signAndStampData: ISignAndStampData,
  ) => {
    const { category, isSignature, blob: fileBlob } = signAndStampData;
    try {
      let blob;
      if (category == FileCategory.SIGN && isSignature) {
        blob = fileBlob as Blob;
      } else {
        const compressedFile = await imageCompression(file, options);
        blob = new Blob([compressedFile], { type: file?.type });
      }
      await Storage.put(uploadPath, blob, {
        contentType: file?.type,
        level: defaultProtectionLevel,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: useFileStorage.tsx ~ line 557 ~ useFileStorage ~');
    }
  };

  const uploadSignAndStamp = async (
    folderName: string,
    file: File,
    signAndStampData: ISignAndStampData,
    protectionLevel = defaultProtectionLevel,
  ) => {
    const {
      updatedName,
      category,
      isSignature,
      blob: fileBlob,
      isSaveForFuture,
      isInspection,
      inspectionId,
      taskId,
    } = signAndStampData;
    let { ext } = path.parse(updatedName);
    const name = path.parse(updatedName)?.name;
    let blob;
    const localStorageSelectedSiteId = getLocalStorageSelectedSiteId();
    const localStorageSelectedDisciplineId = getLocalStorageSelectedDisciplineId();
    const localStorageCurrentUserId = getLocalStorageCurrentUserId();
    ext = ext?.toLowerCase();
    const contentType = getContentType(ext);
    if (category == FileCategory.SIGN && isSignature) {
      blob = fileBlob as Blob;
    } else {
      let compressedFile = file;
      if (contentType === 'image/jpeg' || contentType === 'image/png') {
        compressedFile = await imageCompression(file, options);
      }
      blob = new Blob([compressedFile], { type: contentType });
    }
    const uploadPath = `${folderName}/${name}-${Date.now()}${ext}`;

    const signAndStampfileObj: SignAndStampFile = {
      fileId: `0`,
      inspectionId: inspectionId
        ? inspectionId.toString()
        : isSaveForFuture
        ? '0'
        : `${CurrentUserSelected.inspectionId ? CurrentUserSelected.inspectionId : '0'}`,
      fileUUID: uuidv4(),
      fileCategory: category,
      drawingType: 11,
      isImageFile: getImageType(contentType),
      pcsId: inspectionId
        ? inspectionId.toString()
        : isSaveForFuture
        ? '0'
        : `${CurrentUserSelected.inspectionId ? CurrentUserSelected.inspectionId : '0'}`,
      sizeMb: blob?.size,
      originalPath: `${uploadPath}`,
      extension: blob?.type,
      name: `${updatedName}`,
      originalFileName: `${updatedName}`,
      allowSpaccess: true,
      legacyId: 0,
      fkAssetInspectionTask:
        isSaveForFuture || isInspection ? 0 : taskId ? taskId : CurrentUserSelected.taskId,
      siteId: Number(localStorageSelectedSiteId),
      disciplineId: Number(localStorageSelectedDisciplineId),
      fileDescription: '',
      showOnReport: false,
      isDelete: FALSE,
      isSaveForFuture: isSaveForFuture ? isSaveForFuture : false,
      userId: isSaveForFuture ? localStorageCurrentUserId : 0,
      dateCreated: '',
      dateModified: '',
    };
    switch (category) {
      case FileCategory.SIGN:
      case FileCategory.STAMP:
        {
          await addFileToUploadCache(
            signAndStampfileObj.fileUUID,
            uploadPath,
            signAndStampfileObj,
            blob,
            protectionLevel,
            contentType,
          );
          if (HandleGesture.bulkUpload) {
            return signAndStampfileObj;
          }
          insertIntoSignAndStampFileRxdb(signAndStampfileObj);
        }
        break;
      default:
        throw new Error(`Failed insertion in RXDB`);
    }
    if (!online) return { key: uploadPath };
    try {
      await Storage.put(uploadPath, blob, {
        contentType,
        level: protectionLevel,
      });
    } catch (error) {
      console.log('🚀 ~ file: useFileStorage.tsx ~ line 659 ~ uploadSignAndStamp ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useFileStorage.tsx ~ line 659 ~ uploadSignAndStamp-storagePut- ~',
      );
      return { key: uploadPath };
    }
  };

  const deleteFileFromAttachmentStore = (attachmentId: string) => {
    store.removeItem(attachmentId); //removing mapping from the upload queue if it exists
    return attachmentStore.removeItem(attachmentId);
  };

  const retryFileDownload = async (
    filePath: string,
    attachmentStoreKey: string,
    storeIntoIndexDB = true,
    storeDownloadedTime = false,
  ) => {
    const preSignedUrl = await getImage(filePath);
    const response = await fetch(preSignedUrl as string);
    const blob = await response.blob();
    if (storeDownloadedTime) {
      activeDeficiencyFileStore.setItem(filePath, {
        timestamp: Date.now(),
      });
    }
    return storeIntoIndexDB ? attachmentStore.setItem(attachmentStoreKey, blob) : blob;
  };

  const getBlobForBulkSignAndStamp = async (signAndStampData: ISignAndStampData) => {
    const { updatedName, fileData } = signAndStampData;
    let blob;

    // fetch content type
    let { ext } = path.parse(updatedName);
    ext = ext?.toLowerCase();
    const contentType = getContentType(ext);

    // convert file into blob
    if (fileData) {
      let compressedFile = fileData;
      if (contentType === 'image/jpeg' || contentType === 'image/png') {
        compressedFile = await imageCompression(fileData, options);
      }
      blob = new Blob([compressedFile], { type: contentType });
      return blob;
    }
    return null;
  };

  const saveBlobForBulkSignAndStamp = async (signOrStampFile: ISignAndStampData) => {
    const { updatedName, category, blob, inspectionId, taskId } = signOrStampFile;
    const { name, ext } = path.parse(updatedName);
    const contentType = getContentType(ext);

    const folderName = `signAndStamp/${taskId || inspectionId || ''}`;
    const uploadPath = `${folderName}/${name}-${Date.now()}${ext}`;

    const signAndStampfileObj: SignAndStampFile = {
      fileId: `0`,
      inspectionId: String(inspectionId) || '0',
      fileUUID: uuidv4(),
      fileCategory: category,
      drawingType: 11,
      isImageFile: getImageType(contentType),
      pcsId: String(inspectionId) || '0',
      sizeMb: blob?.size || -1,
      originalPath: uploadPath,
      extension: blob?.type || '',
      name: updatedName,
      originalFileName: updatedName,
      allowSpaccess: true,
      legacyId: 0,
      fkAssetInspectionTask: taskId || 0,
      siteId: SiteDiscipline.siteId,
      disciplineId: SiteDiscipline.disciplineId,
      fileDescription: '',
      showOnReport: false,
      isDelete: FALSE,
      isSaveForFuture: false,
      userId: 0,
    };

    await addFileToUploadCache(
      signAndStampfileObj.fileUUID,
      uploadPath,
      signAndStampfileObj,
      blob,
      defaultProtectionLevel,
      contentType,
    );
    return signAndStampfileObj;
  };

  return {
    uploadFilesInCache,
    getImage,
    getImageInUploadQueue,
    getAttachmentFromIndexDB,
    clearAttachmentStore,
    bulkUploadToS3,
    uploadDeficiencyFile,
    uploadInspectionFile,
    uploadSignAndStamp,
    deleteFileFromAttachmentStore,
    retryFileDownload,
    getBlobForBulkSignAndStamp,
    saveBlobForBulkSignAndStamp,
  };
};

export default useFileStorage;
